import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CRON_PRESETS, TIMEZONES, DELIVERY_CHANNELS, describeCron } from "@/lib/cron";
import { REPORTS } from "@/constants/testIds";

export const DEFAULT_SCHEDULE = {
  cron: "0 9 * * *",
  timezone: "UTC",
  delivery: "email",
  recipients: "",
  slackUrl: "",
  webhookUrl: "",
};

// Normalize the form's local shape into the API schedule payload.
export function toSchedulePayload(f) {
  return {
    cron: f.cron,
    timezone: f.timezone,
    delivery: f.delivery,
    recipients:
      f.delivery === "email"
        ? (f.recipients || "").split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    slack_webhook_url: f.delivery === "slack" ? f.slackUrl || null : null,
    webhook_url: f.delivery === "webhook" ? f.webhookUrl || null : null,
  };
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export function ScheduleForm({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const isPreset = CRON_PRESETS.some((p) => p.value === value.cron);

  return (
    <div className="space-y-4">
      <Field label="Frequency">
        <Select
          value={isPreset ? value.cron : "custom"}
          onValueChange={(v) => set({ cron: v === "custom" ? value.cron : v })}
        >
          <SelectTrigger data-testid={REPORTS.cronPreset} className="bg-card/60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CRON_PRESETS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
            <SelectItem value="custom">Custom (cron expression)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {!isPreset && (
        <Field label="Cron expression">
          <Input
            data-testid={REPORTS.cronCustom}
            value={value.cron}
            onChange={(e) => set({ cron: e.target.value })}
            placeholder="0 9 * * *"
            className="bg-card/60 font-mono"
          />
        </Field>
      )}

      <div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2">
        <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Runs</span>
        <p data-testid={REPORTS.cronPreview} className="text-sm text-accent">
          {describeCron(value.cron)} · {value.timezone}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Timezone">
          <Select value={value.timezone} onValueChange={(v) => set({ timezone: v })}>
            <SelectTrigger data-testid={REPORTS.tzSelect} className="bg-card/60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz} value={tz}>{tz}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Deliver via">
          <Select value={value.delivery} onValueChange={(v) => set({ delivery: v })}>
            <SelectTrigger data-testid={REPORTS.deliverySelect} className="bg-card/60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELIVERY_CHANNELS.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {value.delivery === "email" && (
        <Field label="Recipients (comma-separated)">
          <Input
            data-testid={REPORTS.recipients}
            value={value.recipients}
            onChange={(e) => set({ recipients: e.target.value })}
            placeholder="alex@acme.com, ops@acme.com"
            className="bg-card/60"
          />
        </Field>
      )}
      {value.delivery === "slack" && (
        <Field label="Slack incoming webhook URL">
          <Input
            data-testid={REPORTS.slackUrl}
            value={value.slackUrl}
            onChange={(e) => set({ slackUrl: e.target.value })}
            placeholder="https://hooks.slack.com/services/…"
            className="bg-card/60 font-mono text-xs"
          />
        </Field>
      )}
      {value.delivery === "webhook" && (
        <Field label="Webhook URL (POST)">
          <Input
            data-testid={REPORTS.webhookUrl}
            value={value.webhookUrl}
            onChange={(e) => set({ webhookUrl: e.target.value })}
            placeholder="https://api.acme.com/sema-hook"
            className="bg-card/60 font-mono text-xs"
          />
        </Field>
      )}
    </div>
  );
}
